import type { TFunction } from 'i18next';

import { getImageUrlWithBucketPrefix } from '../../utils/images';
import { formatNumber, localizeDecimalSeparator } from '../../utils/numberFormat';
import { formatAmountByCoinType } from '../../utils/prize';
import type { CoinType } from '../cointype/CoinTypeStore';
import type { BundleItem } from '../rewards/types';

import { selectClosingReward } from './closingReward';
import { clampPct } from './leaderboardRows';
import { resolvePlayableGame } from './playableGame';
import { type BattlePrizeItem, selectBattlePrizeItems } from './prizeItems';
import type { BattleClosing, BattleContender, BattlePrizePool, SlotBattle } from './types';

/**
 * The battle's Rive file (`optInImageUrl`) draws every screen from one contract (Gil's "Battle of the
 * Slots: Rive developer contract"): the opening, the win popup, and NEXT TIME for a player the battle
 * paid nothing.
 *
 * - `MainVM` on `Artboard 1` / `State Machine 1`; `user_click` fires on the CTA of every screen.
 * - The screen is the `screen` enum (`THUMBNAILS` / `REWARDS` / `NEXT_TIME`), and nothing draws until
 *   the `activate` trigger fires, after play. Counts, enums and reward types are read once, at start, so
 *   all is set before play.
 * - Slot cards are image assets: `card_placeholder_0..4` for the cards (`thumbnailN.placeholderNum = N`)
 *   and `card_placeholder_w` for the winner (`thumbnailw.placeholderNum = 5`), swapped by name at load.
 *   Each is fetched first (`preloadBattleThumbnails`): one that does not load keeps the file's own art.
 * - The prize panel is `prizePool`. Coins alone use the `PRIZE_POOL` layout (`prizePoolGC` /
 *   `prizePoolSC`, an empty string hides that coin). A prize with bundle items uses the `GRAND_PRIZE`
 *   row: `rewardsNum` items `reward0..`, each a `rewardType` (`GC`, `SC` or `PLACEHOLDER_N`) and an
 *   `amount`. A `PLACEHOLDER_N` item draws the image asset `placeholder_N`: free spins with their count,
 *   mini-game rounds as their art only, metagame tokens with their amount.
 * - The copy is the app's, in the player's language: the button text `cta` and the four titles
 *   (`title_prize_pool`, `title_your_reward`, `title_winner`, `title_next_time`).
 * - GS-1032 adds `rankText` ("#3" on the win screen, empty hides it), `title_your_share` and
 *   `title_winning_slot`. Each is written only when the file has it, so the file in production keeps
 *   working until the new one ships (contract §10.6).
 * - The compensation screen (`SCREEN.COMPENSATION`) is not in any file yet: a compensation closing is
 *   drawn in code for now (`CompensationClosing`).
 * - `user_click` fires on the CTA and plays the close; `finish` fires when the close ends, and only then
 *   does the app act on the tap and remove the view.
 */
export type BattleRiveScreen = 'opening' | 'win' | 'next_time';

export const BATTLE_RIVE_PATHS = {
  screen: 'screen',
  activate: 'activate',
  thumbnailsNum: 'thumbnailsNum',
  rewardGroup: 'prizePool/rewardGroup',
  rewardsNum: 'prizePool/rewardsNum',
  prizePoolGC: 'prizePool/prizePoolGC',
  prizePoolSC: 'prizePool/prizePoolSC',
  winnerPercentage: 'winnerPercentage',
  ctaText: 'cta',
  cta: 'user_click',
  finish: 'finish',
} as const;

/** The four titles the file shows, by screen. */
export const BATTLE_RIVE_TITLE_PATHS = {
  prizePool: 'title_prize_pool',
  yourReward: 'title_your_reward',
  winner: 'title_winner',
  nextTime: 'title_next_time',
} as const;

export type BattleRiveTitles = Record<keyof typeof BATTLE_RIVE_TITLE_PATHS, string>;

/** GS-1032's strings. Written only when the file has them: older files lack them and must still load. */
export const BATTLE_RIVE_OPTIONAL_PATHS = {
  rankText: 'rankText',
  yourShare: 'title_your_share',
  winningSlot: 'title_winning_slot',
} as const;

export type BattleRiveOptionalText = Record<keyof typeof BATTLE_RIVE_OPTIONAL_PATHS, string>;

/** The card `thumbnail0..4` (index) or the winner's card `thumbnailw` (`'w'`): which image it shows. */
export const battleRiveThumbnailPath = (card: number | 'w') => `thumbnail${card}/placeholderNum`;

/** One item of the `GRAND_PRIZE` row. */
export const battleRiveRewardPaths = (index: number) => ({
  type: `prizePool/reward${index}/rewardType`,
  amount: `prizePool/reward${index}/amount`,
});

/** The `SCREEN` enum's value for each screen. */
export const BATTLE_RIVE_SCREEN_VALUES: Record<BattleRiveScreen, string> = {
  opening: 'THUMBNAILS',
  win: 'REWARDS',
  next_time: 'NEXT_TIME',
};

export type BattleRiveRewardGroup = 'PRIZE_POOL' | 'GRAND_PRIZE';

/** The winner's card shows image 5, `card_placeholder_w`. */
export const BATTLE_RIVE_WINNER_PLACEHOLDER = 5;

export const battleRiveCardAsset = (card: number | 'w') => `card_placeholder_${card}`;

export const battleRiveRewardAsset = (slot: number) => `placeholder_${slot}`;

const MIN_THUMBNAILS = 2;
const MAX_THUMBNAILS = 5;
/** The `GRAND_PRIZE` row has five items, and five reward images. */
export const MAX_BATTLE_RIVE_REWARDS = 5;

export interface BattleRiveReward {
  /** `GC`, `SC` or `PLACEHOLDER_N`. */
  type: string;
  /** The label under the icon; empty draws the icon alone, larger. */
  amount: string;
}

export interface BattleRiveData {
  screen: BattleRiveScreen;
  thumbnailsNum: number;
  /** Image asset name → URL. Assets left out keep the art embedded in the file. */
  images: Record<string, string>;
  rewardGroup: BattleRiveRewardGroup;
  /** `PRIZE_POOL` coins; empty hides that coin. */
  prizeGC: string;
  prizeSC: string;
  /** The `GRAND_PRIZE` row, left to right; empty for `PRIZE_POOL`. */
  rewards: BattleRiveReward[];
  /** Empty on the opening; set anyway, as the file's own default is placeholder text. */
  winnerPercentage: string;
  titles: BattleRiveTitles;
  optionalText: BattleRiveOptionalText;
}

const byDisplayOrder = (contenders: BattleContender[]) =>
  [...contenders].sort((a, b) => a.displayOrder - b.displayOrder);

/** The card art for a slot: what the podium shows, i.e. its stand-in game while its own is down. */
const thumbnailOf = (contender: BattleContender): string | null => {
  const { thumbnailUrl } = resolvePlayableGame(contender);

  return thumbnailUrl ? getImageUrlWithBucketPrefix(thumbnailUrl) : null;
};

const findWinner = (battle: SlotBattle): BattleContender | null => {
  const winner = battle.closing?.winner;

  return winner ? (battle.contenders.find((contender) => contender.gameId === winner.gameId) ?? null) : null;
};

/** One bundle item as a `PLACEHOLDER_N` reward: its art, and the label under it. */
interface BattleRiveRewardArt {
  imageUrl: string;
  amount: string;
}

/**
 * Each bundle item as the prize row draws it elsewhere (`selectBattlePrizeItems`): casino free spins with
 * their count, a mini-game round as its art only, a metagame token with its amount, anything else as its
 * widget art. An item with no art has nothing to draw and is left out.
 */
const rewardArtOf = (prize: BattlePrizeItem): BattleRiveRewardArt | null => {
  switch (prize.kind) {
    case 'image':
      return { imageUrl: prize.imageUrl, amount: '' };
    case 'freeSpins': {
      const { bundlePayload } = prize.item;
      const imageUrl = bundlePayload.gameImageUrl || bundlePayload.imageUrl;
      const count = bundlePayload.displayCount ?? bundlePayload.rounds ?? 0;

      return imageUrl ? { imageUrl, amount: count > 0 ? formatNumber(count) : '' } : null;
    }
    case 'metaToken': {
      const { currencySymbol, amount } = prize.item.bundlePayload;

      return currencySymbol ? { imageUrl: currencySymbol, amount: amount ? formatNumber(amount) : '' } : null;
    }
    case 'widget': {
      const imageUrl = prize.item.widgetImageUrl || prize.item.popupImageUrl;

      return imageUrl ? { imageUrl, amount: '' } : null;
    }
    default:
      return null;
  }
};

/** The prize's bundle items the `GRAND_PRIZE` row can draw, in their order. */
export const selectBattleRiveRewardArt = (bundleItems: BundleItem[] | undefined): BattleRiveRewardArt[] =>
  selectBattlePrizeItems(bundleItems)
    .map(rewardArtOf)
    .filter((art): art is BattleRiveRewardArt => art !== null);

/**
 * The closing's Rive screen: the win popup for a winner, NEXT TIME for a player under the threshold.
 * Null for compensation, which no file draws yet (contract §10.5: `CompensationClosing` in code).
 */
export const closingRiveScreen = (closing: BattleClosing): Exclude<BattleRiveScreen, 'opening'> | null => {
  switch (closing.outcome) {
    case 'won':
      return 'win';
    case 'lost_no_reward':
      return 'next_time';
    default:
      return null;
  }
};

/**
 * The opening advertises the prize pool, coins only (GS-1032). The win popup shows what CLAIM credits —
 * the player's own rank package, not the pool, which would read as all of it being theirs.
 */
export const selectBattleRivePrize = (battle: SlotBattle, screen: BattleRiveScreen): BattlePrizePool => {
  if (screen === 'opening') {
    return {
      gc: battle.prizePool?.gc ?? null,
      sc: battle.prizePool?.sc ?? null,
      bundleItems: [],
    };
  }

  if (screen === 'next_time') {
    return { gc: null, sc: null, bundleItems: [] };
  }

  const closing = battle.closing;
  const reward = closing ? selectClosingReward(closing) : null;

  return reward
    ? { gc: reward.gcCoin, sc: reward.scCoin, bundleItems: reward.bundleItems }
    : { gc: null, sc: null, bundleItems: [] };
};

/** Only a `.riv` is a battle Rive: a run still holding the opening image it had before has none. */
export function hasBattleRive(battle: SlotBattle): boolean {
  return Boolean(battle.optInImageUrl && /\.riv(?:[?#].*)?$/i.test(battle.optInImageUrl));
}

/**
 * Whether the battle can be drawn on this screen at all: a Rive file, and for a closing a winner that is
 * one of the battle's contenders (the win popup also needs a prize to show). False sends the opening to
 * be skipped and the win popup to its text pane.
 */
export function canDrawBattleRive(battle: SlotBattle, screen: BattleRiveScreen): boolean {
  if (!hasBattleRive(battle)) {
    return false;
  }

  if (screen === 'opening') {
    return true;
  }

  if (screen === 'next_time') {
    return battle.closing?.outcome === 'lost_no_reward' && findWinner(battle) !== null;
  }

  const prize = selectBattleRivePrize(battle, 'win');
  const hasPrize = Boolean(prize.gc || prize.sc) || selectBattleRiveRewardArt(prize.bundleItems).length > 0;

  return battle.closing?.outcome === 'won' && findWinner(battle) !== null && hasPrize;
}

const formatCoin = (amount: number | null | undefined, coinType: CoinType, t: TFunction): string =>
  amount ? formatAmountByCoinType({ amount, coinType, t }) : '';

export function buildBattleRiveData(battle: SlotBattle, screen: BattleRiveScreen, t: TFunction): BattleRiveData {
  const contenders = byDisplayOrder(battle.contenders).slice(0, MAX_THUMBNAILS);
  const images: Record<string, string> = {};

  contenders.forEach((contender, index) => {
    const url = thumbnailOf(contender);

    if (url) {
      images[battleRiveCardAsset(index)] = url;
    }
  });

  const winner = screen === 'opening' ? null : findWinner(battle);
  const winnerUrl = winner ? thumbnailOf(winner) : null;

  if (winnerUrl) {
    images[battleRiveCardAsset('w')] = winnerUrl;
  }

  const prize = selectBattleRivePrize(battle, screen);
  const prizeGC = formatCoin(prize.gc, 'GC', t);
  const prizeSC = formatCoin(prize.sc, 'SC', t);
  const art = selectBattleRiveRewardArt(prize.bundleItems);
  const sharePct = screen === 'opening' ? undefined : battle.closing?.winner?.sharePct;
  const winnerPercentage =
    sharePct === undefined
      ? ''
      : t('slotBattle.sharePct', { pct: localizeDecimalSeparator(clampPct(sharePct).toFixed(1)) });
  const thumbnailsNum = Math.min(Math.max(contenders.length, MIN_THUMBNAILS), MAX_THUMBNAILS);
  const titles: BattleRiveTitles = {
    prizePool: t('slotBattle.prizePoolLabel'),
    yourReward: t('slotBattle.yourRewardLabel'),
    winner: t('slotBattle.riveWinnerTitle'),
    nextTime: t('slotBattle.riveNextTimeTitle'),
  };
  const rank = screen === 'win' ? battle.closing?.rank : null;
  const optionalText: BattleRiveOptionalText = {
    rankText: rank ? t('slotBattle.rankText', { rank }) : '',
    yourShare: t('slotBattle.titleYourShare'),
    winningSlot: t('slotBattle.titleWinningSlot'),
  };

  if (art.length === 0) {
    return {
      screen,
      thumbnailsNum,
      images,
      rewardGroup: 'PRIZE_POOL',
      prizeGC,
      prizeSC,
      rewards: [],
      winnerPercentage,
      titles,
      optionalText,
    };
  }

  // The coins lead the row, then the bundle items, each on its own reward image.
  const rewards: BattleRiveReward[] = [];

  if (prizeGC) {
    rewards.push({ type: 'GC', amount: prizeGC });
  }

  if (prizeSC) {
    rewards.push({ type: 'SC', amount: prizeSC });
  }

  art.slice(0, MAX_BATTLE_RIVE_REWARDS - rewards.length).forEach(({ imageUrl, amount }, slot) => {
    images[battleRiveRewardAsset(slot)] = getImageUrlWithBucketPrefix(imageUrl);
    rewards.push({ type: `PLACEHOLDER_${slot}`, amount });
  });

  return {
    screen,
    thumbnailsNum,
    images,
    rewardGroup: 'GRAND_PRIZE',
    prizeGC: '',
    prizeSC: '',
    rewards,
    winnerPercentage,
    titles,
    optionalText,
  };
}

/** How long the thumbnails get, all together, before the file loads without the ones still out. */
export const BATTLE_THUMBNAIL_PRELOAD_TIMEOUT_MS = 3000;

export interface SkippedBattleThumbnail {
  asset: string;
  url: string;
  reason: string;
}

export interface PreloadedBattleThumbnails {
  /** Image asset name → URL, for the thumbnails that came back: the only ones worth swapping in. */
  loaded: Record<string, string>;
  skipped: SkippedBattleThumbnail[];
}

/**
 * Fetches each thumbnail before the file loads, as the swap itself does (same request, so the swap
 * then reads it from the HTTP cache). The file's asset loader has to claim or leave an asset on the
 * spot, and a claimed asset whose swap fails is a blank card — so only thumbnails known to load are
 * claimed. A missing one (a 404 in the bucket), a network error or one still out at the deadline is
 * skipped, never thrown: its card keeps the art embedded in the file. Callers pass allowed URLs only.
 */
export async function preloadBattleThumbnails(
  images: Record<string, string>,
  timeoutMs: number = BATTLE_THUMBNAIL_PRELOAD_TIMEOUT_MS,
): Promise<PreloadedBattleThumbnails> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  // The winner is also one of the cards: fetch each URL once.
  const requests = new Map<string, Promise<string | null>>();

  const load = async (url: string): Promise<string | null> => {
    try {
      const response = await fetch(url, { signal: controller.signal });

      if (!response.ok) {
        return `HTTP ${response.status}`;
      }

      await response.blob();
      return null;
    } catch (error) {
      if (controller.signal.aborted) {
        return `timed out after ${timeoutMs} ms`;
      }

      return error instanceof Error ? error.message : String(error);
    }
  };

  try {
    const outcomes = await Promise.all(
      Object.entries(images).map(async ([asset, url]) => {
        let request = requests.get(url);

        if (!request) {
          request = load(url);
          requests.set(url, request);
        }

        return { asset, url, reason: await request };
      }),
    );

    const loaded: Record<string, string> = {};
    const skipped: SkippedBattleThumbnail[] = [];

    outcomes.forEach(({ asset, url, reason }) => {
      if (reason === null) {
        loaded[asset] = url;
      } else {
        skipped.push({ asset, url, reason });
      }
    });

    return { loaded, skipped };
  } finally {
    clearTimeout(timer);
  }
}
